<template>
  <div class="columns-container">
    <div class="column">
      <quote-list-block>
        <template #header>
          <div class="title">{{ i18n.t('/Configuration/Interior') }}</div>
        </template>
        <template #body>
          <configuration-feature
            :feature="originalUpholstery"
            :can-detect-feature-change="true"
            :original-features="editableUpholstery"
            :feature-label="i18n.t('/Configuration/Upholstery')"
          />
          <configuration-feature
            :feature="originalUpholsteryColor"
            :can-detect-feature-change="true"
            :original-feature="editableUpholsteryColor"
            :feature-label="i18n.t('/Configuration/Color')"
          />
          <template v-if="originalInteriorOptions">
            <configuration-feature
              v-for="feature in originalInteriorOptions"
              :key="feature.Id"
              :can-detect-feature-change="true"
              :original-features="editableInteriorOptions"
              :feature="feature"
              :feature-label="i18n.t('/Configuration/Interior Package')"
            />
          </template>
        </template>
      </quote-list-block>
    </div>
    <div class="column">
      <quote-list-block>
        <template #header>
          <div class="title">{{ i18n.t('/Configuration/Interior') }}</div>
          <div
            v-if="canChangeInterior.Allowed"
            class="edit-dialog"
            @click="showChangeOptionsDialog"
          >
            <i class="fa-solid fa-pencil"></i>
          </div>
          <div
            v-if="!canChangeInterior.Allowed && canChangeInterior.Reason !== ''"
            class="edit-dialog"
            :title="canChangeInterior.Reason"
          >
            <i class="fa-solid fa-lock"></i>
          </div>
        </template>
        <template #body>
          <configuration-feature
            v-for="feature in editableUpholstery"
            :key="feature.Id"
            :can-detect-feature-change="true"
            :original-feature="originalUpholstery"
            :feature="feature"
            :feature-label="i18n.t('/Configuration/Upholstery')"
          />
          <configuration-feature
            :can-detect-feature-change="true"
            :original-feature="originalUpholsteryColor"
            :feature="editableUpholsteryColor"
            :feature-label="i18n.t('/Configuration/Color')"
          />
          <template v-if="editableInteriorOptions">
            <configuration-feature
              v-for="feature in editableInteriorOptions"
              :key="feature.Id"
              :can-detect-feature-change="true"
              :original-features="originalInteriorOptions"
              :feature="feature"
              :feature-label="i18n.t('/Configuration/Interior Package')"
            />
          </template>
        </template>
      </quote-list-block>
    </div>
    <multiple-feature-change-dialog
      v-if="showOptionsChangeDialog"
      :title="i18n.t('/change-order/configuration/change-options')"
      :available-items="availableOptions"
      :selected-items="changedOptions"
      @close="showOptionsChangeDialog = false"
      @confirm="confirmOptionsChange"
    />
  </div>
</template>

<script>
import QuoteListBlock from '@/components/generic/presentational/molecules/quoteListBlock/QuoteListBlock.vue'
import ConfigurationFeature from '@/components/changeOrder/presentational/organisms/configurationFeature/ConfigurationFeature.vue'
import MultipleFeatureChangeDialog from '@/components/changeOrder/presentational/organisms/multipleFeatureChangeDialog/multipleFeatureChangeDialog.vue'
import { mapGetters } from 'vuex'
import ChangeOrderStatus from '@/helpers/enums/changeOrderStatus'

export default {
  components: {
    MultipleFeatureChangeDialog,
    ConfigurationFeature,
    QuoteListBlock,
  },
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      showOptionsChangeDialog: false,
    }
  },
  computed: {
    ...mapGetters({
      changeOrder: 'changeOrder/get',
    }),
    originalUpholstery() {
      return this.getInteriorOption(
        this.changeOrder.CarConfiguration.OriginalOptions,
        'INTERIOR_UPHOLSTERY'
      )
    },
    originalUpholsteryColor() {
      return this.getInteriorOption(
        this.changeOrder.CarConfiguration.OriginalOptions,
        'INTERIOR_UPHOLSTERY_COLOR'
      )
    },
    originalInteriorOptions() {
      return this.getInteriorOptions(
        this.changeOrder.CarConfiguration.OriginalOptions,
        'INTERIOR_OPTIONS'
      )
    },
    editableUpholstery() {
      return this.getInteriorOptions(
        this.changeOrder.CarConfiguration.ChangedOptions,
        'INTERIOR_UPHOLSTERY'
      )
    },
    editableUpholsteryColor() {
      return this.getInteriorOption(
        this.changeOrder.CarConfiguration.ChangedOptions,
        'INTERIOR_UPHOLSTERY_COLOR'
      )
    },
    editableInteriorOptions() {
      return this.getInteriorOptions(
        this.changeOrder.CarConfiguration.ChangedOptions,
        'INTERIOR_OPTIONS'
      )
    },
    changedOptions() {
      const interiorTypes = ['INTERIOR_UPHOLSTERY', 'INTERIOR_UPHOLSTERY_COLOR', 'INTERIOR_OPTIONS']
      return this.changeOrder.CarConfiguration.ChangedOptions.filter((option) =>
        interiorTypes.includes(option.Type)
      )
    },
    availableOptions() {
      const interiorTypes = ['INTERIOR_UPHOLSTERY', 'INTERIOR_UPHOLSTERY_COLOR', 'INTERIOR_OPTIONS']
      let filteredOptions = this.changeOrder.CarConfiguration.AvailableOptions.filter((option) =>
        interiorTypes.includes(option.Type)
      )

      return this.sortOptionsBy(filteredOptions, 'shortId')
    },
    canChangeInterior() {
      if (
        this.changeOrder.MainOrderStatus === ChangeOrderStatus.submitted ||
        this.changeOrder.MainOrderStatus === ChangeOrderStatus.archived
      ) {
        return {
          Allowed: false,
          Reason: 'Order has been submitted or deleted',
        }
      }

      if (this.changeOrder.CarConfiguration.HasUnknownOkapiModel) {
        return {
          Allowed: false,
          Reason:
            'Het is voor dit voertuig niet mogelijk om configuratiewijzigingen door te voeren. ' +
            'Neem contact op met DSS',
        }
      }

      return this.changeOrder.Permissions.CanChangeInterior
    },
  },
  methods: {
    getInteriorOption(options, optionType) {
      if (!Array.isArray(options)) {
        return null
      }
      return options.find((option) => option.Type === optionType)
    },
    getInteriorOptions(options, optionType) {
      if (!Array.isArray(options)) {
        return []
      }
      return options.filter((option) => option.Type === optionType)
    },
    showChangeOptionsDialog() {
      this.showOptionsChangeDialog = true
    },
    confirmOptionsChange(changedOptions) {
      const interiorTypes = ['INTERIOR_UPHOLSTERY', 'INTERIOR_UPHOLSTERY_COLOR', 'INTERIOR_OPTIONS']
      let interiorOptions = this.changeOrder.CarConfiguration.ChangedOptions.filter(
        (option) => !interiorTypes.includes(option.Type)
      )

      this.changeOrder.CarConfiguration.ChangedOptions = interiorOptions.concat(changedOptions)

      this.$store.commit('changeOrder/setCarConfigurationStatus')
      this.showOptionsChangeDialog = false
    },
    sortOptionsBy(options, key) {
      return options.sort((a, b) => {
        let valA = a[key]
        let valB = b[key]
        if (valA < valB) {
          return -1
        }
        if (valA > valB) {
          return 1
        }
        return 0
      })
    },
  },
}
</script>

<style lang="scss" scoped>
:deep(.row) {
  grid-template-columns: auto 4em 8em 1em 2em;
  grid-template-areas: 'title code price-incl info-icon edit-dialog';
}

.info-icon {
  cursor: pointer;
}

.edit-dialog {
  cursor: pointer;
}

.columns-container {
  display: flex;
  justify-content: space-between; /* Distribute columns evenly */
}

.column {
  flex: 1;
  margin: 0 10px;
}
</style>
